import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SeamConnectWebview {
  connect_webview_id: string;
  url: string;
  status: string;
}

export interface SeamConnectWebviewStatus {
  connect_webview_id: string;
  status: string;
  connected_account_id: string | null;
  provider: string | null;
}

export interface RemoteAccessDevice {
  id: number;
  device_name: string;
  in_use: boolean;
  remote_access_address_id?: number;
  display_name?: string;
  device_type?: string;
  address_ids?: number[];
}

export interface RemoteAccessDeviceWithAssignments {
  id: number;
  remote_access_address_id: number;
  device_name: string;
  display_name: string;
  device_type: string;
  in_use: boolean;
  address_ids: number[];
}

export interface RemoteAccessAddressAddress {
  id: number;
  address1?: string;
  address2?: string;
  address_name?: string;
}

export interface RemoteAccessAddress {
  id: number;
  name: string | null;
  address1: string | null;
  address2: string | null;
  address_ids: number[];
  addresses: RemoteAccessAddressAddress[];
  remote_access_devices: RemoteAccessDevice[];
}

export interface SeamIntegration {
  id: number;
  type: string;
  key: string;
  integration_data: {
    connected_account_id: string;
    account_type_display_name?: string;
    image_url?: string;
  };
}

export interface SeamAccessCode {
  code: string;
  starts_at: string;
  ends_at: string;
  source_access_code_id: string;
}

export interface GenerateAccessCodeParams {
  starts_at?: string;
  ends_at?: string;
  guest_reservation_id?: number;
}

export interface RemoteAccessAddressChange {
  id: number;
  address_ids: number[];
}

@Injectable({ providedIn: 'root' })
export class SeamService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.api_url;

  createConnectWebview(): Promise<SeamConnectWebview> {
    return firstValueFrom(
      this.http.post<SeamConnectWebview>(
        `${this.baseUrl}api/v1/customer/customer-integrations/create-seam-connect-webview`,
        {}
      )
    );
  }

  getConnectWebviewStatus(connectWebviewId: string): Promise<SeamConnectWebviewStatus> {
    return firstValueFrom(
      this.http.get<SeamConnectWebviewStatus>(
        `${this.baseUrl}api/v1/customer/customer-integrations/seam-connect-webview`,
        { params: { connect_webview_id: connectWebviewId } }
      )
    );
  }

  createIntegration(connectedAccountId: string): Promise<SeamIntegration> {
    return firstValueFrom(
      this.http.post<SeamIntegration>(
        `${this.baseUrl}api/v1/customer/customer-integrations`,
        {
          type: 'remote_property_access',
          key: 'seam',
          data: { connected_account_id: connectedAccountId }
        }
      )
    );
  }

  getRemoteAccessAddresses(): Promise<RemoteAccessAddress[]> {
    return firstValueFrom(
      this.http.get<RemoteAccessAddress[]>(
        `${this.baseUrl}api/v1/customer/remote-access-addresses`
      )
    );
  }

  getRemoteAccessDevices(opts: { unassigned?: boolean } = {}): Promise<RemoteAccessDeviceWithAssignments[]> {
    let params = new HttpParams();
    if (opts.unassigned) {
      params = params.set('filter[unassigned]', 'true');
    }
    return firstValueFrom(
      this.http.get<RemoteAccessDeviceWithAssignments[]>(
        `${this.baseUrl}api/v1/customer/remote-access-devices`,
        { params }
      )
    );
  }

  updateRemoteAccessAddressAddressIds(remoteAccessAddressId: number, addressIds: number[]): Promise<any> {
    return firstValueFrom(
      this.http.put(
        `${this.baseUrl}api/v1/customer/remote-access-addresses/${remoteAccessAddressId}`,
        { address_ids: serializeAddressIds(addressIds) }
      )
    );
  }

  bulkUpdateRemoteAccessAddresses(changes: RemoteAccessAddressChange[]): Promise<any> {
    return firstValueFrom(
      this.http.put(
        `${this.baseUrl}api/v1/customer/remote-access-addresses/update-all`,
        {
          remote_access_addresses_changes: changes.map(change => ({
            id: change.id,
            address_ids: serializeAddressIds(change.address_ids)
          }))
        }
      )
    );
  }

  getExistingIntegration(): Promise<SeamIntegration[]> {
    return firstValueFrom(
      this.http.get<SeamIntegration[]>(
        `${this.baseUrl}api/v1/customer/customer-integrations`,
        { params: { 'filters[key]': 'seam' } }
      )
    );
  }

  generateAccessCode(
    remoteAccessDeviceId: number,
    params: GenerateAccessCodeParams
  ): Promise<SeamAccessCode> {
    return firstValueFrom(
      this.http.post<SeamAccessCode>(
        `${this.baseUrl}api/v1/customer/customer-integrations/remote-access-devices/${remoteAccessDeviceId}/generate-access-code`,
        params
      )
    );
  }
}

// PUT /remote-access-addresses expects address_ids as a comma-separated string ("" to unlink).
function serializeAddressIds(ids: number[]): string {
  return ids.join(',');
}
